import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, MenuItem, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { ApiRequest } from "GlobalFunctions";

const extractYoutubeId = (url: string): string => {
  if (!url) return "";
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})$/,
  ];
  for (const p of patterns) {
    const match = url.match(p);
    if (match) return match[1];
  }
  return "";
};

export default function FaqEditor(props: any) {
  const { openEditor, setOpenEditor, faq_item_id, loadList, setLoading, categories } = props;
  const { enqueueSnackbar } = useSnackbar();

  const defaultValues = {
    faq_item_id: faq_item_id ? faq_item_id : "null",
    faq_category_id: "",
    question: "",
    answer: "",
    video_url: "",
    sort_order: 0,
    active: 1,
  };

  const [formValues, setFormValues] = useState(defaultValues);

  useEffect(() => {
    if (faq_item_id) {
      ApiRequest({
        url: "/faq/details",
        method: "get",
        headers: { ltkn: localStorage.getItem("ltkn") },
        query: { faq_item_id },
        setResponse: (response: any) => {
          setFormValues(response.data);
        },
      });
    } else {
      setFormValues(defaultValues);
    }
  }, [faq_item_id]);

  const handleInputChange = (e: { target: { name: any; value: any } }) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleSubmit = () => {
    if (!formValues.faq_category_id || !formValues.question.trim() || !formValues.answer.trim()) {
      enqueueSnackbar("Completa todos los campos requeridos", { variant: "warning" });
      return;
    }
    setLoading(true);
    const body = new FormData();
    body.append("formValues", JSON.stringify(formValues));

    ApiRequest({
      url: "/faq/addedit",
      method: "post",
      headers: { ltkn: localStorage.getItem("ltkn") },
      body,
      setResponse: (response: any) => {
        if (response.status === "success") {
          enqueueSnackbar("Pregunta guardada", { variant: "success" });
          setOpenEditor(false);
          loadList();
        } else {
          enqueueSnackbar(response.message || "Error al guardar", { variant: "error" });
        }
        setLoading(false);
      },
    });
  };

  return (
    <Dialog open={openEditor} onClose={() => setOpenEditor(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6">{faq_item_id ? "Editar pregunta" : "Nueva pregunta"}</Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 3 }}>
        <TextField
          select
          name="faq_category_id"
          label="Categoria"
          size="small"
          required
          fullWidth
          value={formValues.faq_category_id}
          onChange={handleInputChange}
        >
          {categories.map((cat: any) => (
            <MenuItem key={cat.faq_category_id} value={cat.faq_category_id}>{cat.name}</MenuItem>
          ))}
        </TextField>

        <TextField
          name="question"
          label="Pregunta"
          size="small"
          required
          fullWidth
          value={formValues.question}
          onChange={handleInputChange}
        />

        <TextField
          name="answer"
          label="Respuesta"
          size="small"
          required
          fullWidth
          multiline
          rows={4}
          value={formValues.answer}
          onChange={handleInputChange}
        />

        <div style={{ display: "flex", gap: 16 }}>
          <TextField
            name="sort_order"
            label="Orden"
            size="small"
            type="number"
            value={formValues.sort_order}
            onChange={handleInputChange}
            sx={{ width: 100 }}
          />
          <TextField
            select
            name="active"
            label="Estado"
            size="small"
            value={formValues.active}
            onChange={handleInputChange}
            sx={{ width: 140 }}
          >
            <MenuItem value={1}>Activo</MenuItem>
            <MenuItem value={0}>Inactivo</MenuItem>
          </TextField>
        </div>

        <TextField
          name="video_url"
          label="Video de YouTube (opcional)"
          size="small"
          fullWidth
          placeholder="https://www.youtube.com/watch?v=... o https://youtu.be/..."
          value={formValues.video_url || ""}
          onChange={handleInputChange}
          helperText={formValues.video_url && !extractYoutubeId(formValues.video_url) ? "URL no reconocida" : ""}
          error={!!formValues.video_url && !extractYoutubeId(formValues.video_url)}
        />
        {formValues.video_url && extractYoutubeId(formValues.video_url) && (
          <div style={{ borderRadius: 8, overflow: "hidden", aspectRatio: "16/9", maxHeight: 200 }}>
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${extractYoutubeId(formValues.video_url)}`}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Preview"
            />
          </div>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={() => setOpenEditor(false)} variant="outlined">
          Cancelar
        </Button>
        <Button onClick={handleSubmit} variant="contained">
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
